/**
 * CrmWebSocket - WebSocket connection manager for real-time CRM events.
 *
 * Manages the WebSocket lifecycle including:
 * - Authenticated connection to the CRM event stream
 * - Exponential backoff reconnection with jitter
 * - Catchup on reconnect (sends last_known_event_id)
 * - Inactivity detection (tab hidden for extended period → full refresh)
 * - Heartbeat via ping/pong to detect stale connections
 *
 * This is a plain class (not a hook) so it can be shared across components.
 * Use the useCrmWebSocket hook for React integration.
 */

import { USER_IDLE_THRESHOLD_MS } from '@/lib/activity/constants';
import { getWebSocketBaseUrl } from '@/lib/config';

import type { EntityTypeName } from '../core/types';
import type {
  CrmClientMessage,
  CrmServerMessage,
  CrmWebSocketEvent,
  CrmWebSocketStatus,
} from './types';

/** Configuration for the WebSocket connection. */
interface CrmWebSocketConfig {
  /** Org ID to connect to */
  orgId: number;
  /** Called when events are received (from live stream or catchup) */
  onEvents: (events: CrmWebSocketEvent[]) => void;
  /** Called when a full refresh is required (too many missed events or long inactivity) */
  onFullRefreshRequired: () => void;
  /** Called when connection status changes */
  onStatusChange?: (status: CrmWebSocketStatus) => void;
  /**
   * Called with the current cursor whenever the server CONFIRMS it — a catchup
   * reply (whose access-filtered event list may be empty) or the first pong
   * that seeds the baseline. This fires even when the cursor does not advance
   * past a resumed `initialLastEventId` (an idle wake whose catchup is empty),
   * so the hook can mirror the cursor to its poller-facing ref. Without it, a
   * connected-but-quiet WS leaves that ref null and the poller treats the
   * healthy connection as inactive, invalidating CRM queries on every version
   * change. Live-broadcast batches omit `latest_event_id` and are mirrored via
   * `onEvents` instead, so this is not called on the live path.
   */
  onCursorConfirmed?: (lastEventId: number) => void;
  /**
   * Cursor carried over from a previous instance for the same org (e.g. one
   * destroyed while the tab slept). When set, the first onopen requests
   * catchup from it, so the server either replays the missed events or
   * answers full_refresh_required.
   */
  initialLastEventId?: number | null;
}

/** Reconnect backoff settings */
const MIN_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

/** Heartbeat interval (ms) — send ping to detect stale connections */
const HEARTBEAT_INTERVAL_MS = 30000;

/** How long to wait for a pong before considering the connection dead */
const HEARTBEAT_TIMEOUT_MS = 10000;

/**
 * Add random jitter (±20%) to a delay to prevent thundering herd.
 */
function withJitter(delay: number): number {
  const jitter = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
  return Math.round(delay * jitter);
}

export class CrmWebSocket {
  private ws: WebSocket | null = null;
  private config: CrmWebSocketConfig;
  private lastEventId: number | null = null;
  // Whether any event batch arrived since the last heartbeat ping was sent.
  // Used to distinguish a genuine broadcast stall (zombie connection) from
  // normal in-flight lag when the heartbeat reveals a higher latest_event_id.
  private receivedEventSinceLastPing = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenSince: number | null = null;
  private status: CrmWebSocketStatus = 'disconnected';
  private destroyed = false;
  private shouldNotReconnect = false; // Set to true for auth errors
  private currentSubscription: EntityTypeName[] | null = null;

  constructor(config: CrmWebSocketConfig) {
    this.config = config;
    this.lastEventId = config.initialLastEventId ?? null;
  }

  /**
   * Connect to the WebSocket server.
   * If already connected, disconnects first.
   */
  connect(): void {
    if (this.destroyed) {
      return;
    }
    this.disconnect();

    this.setStatus('connecting');

    const url = `${getWebSocketBaseUrl()}/ws/orgs/${this.config.orgId}/crm-events/`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      // WebSocket constructor can throw if URL is invalid
      console.warn(
        '[CrmWebSocket] WebSocket constructor failed, scheduling reconnect:',
        err
      );
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      if (this.destroyed) {
        return;
      }

      this.setStatus('connected');
      this.reconnectAttempt = 0;

      // Re-subscribe to restricted entity types BEFORE catchup so the server
      // constrains the gap-fill to only subscribed types from the start.
      if (this.currentSubscription !== null) {
        this.send({
          type: 'subscribe',
          entity_types: this.currentSubscription,
        });
      }

      // Request catchup for any events missed while disconnected
      if (this.lastEventId !== null) {
        this.send({ type: 'catchup', last_event_id: this.lastEventId });
      }

      this.startHeartbeat();

      // Send one ping immediately so the first pong seeds lastEventId at t≈0
      // (enables gap-detection on a fresh window before the first 30s heartbeat,
      // and re-seeds after a full_refresh_required reset). Idempotent on reconnect:
      // catchup was already requested above; a duplicate catchup would be harmless.
      this.send({ type: 'ping' });
    };

    this.ws.onmessage = event => {
      if (this.destroyed) {
        return;
      }

      // Cancel heartbeat timeout — connection is alive (we received data)
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }

      try {
        const message = JSON.parse(event.data) as CrmServerMessage;
        this.handleMessage(message);
      } catch {
        console.error('[CrmWebSocket] Failed to parse message:', event.data);
      }
    };

    this.ws.onclose = event => {
      if (this.destroyed) {
        return;
      }

      this.stopHeartbeat();
      this.setStatus('disconnected');

      // Don't reconnect for auth/permission errors
      if (event.code === 4401 || event.code === 4403) {
        console.warn(
          `[CrmWebSocket] Connection rejected (code ${event.code}), not reconnecting`
        );
        this.shouldNotReconnect = true;
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onerror is always followed by onclose, so we handle reconnect there
    };

    // Set up visibility change listener
    this.setupVisibilityListener();
  }

  /**
   * Disconnect from the WebSocket server.
   * Does not trigger reconnection.
   */
  disconnect(): void {
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.removeVisibilityListener(); // Prevent reconnect on visibility change

    if (this.ws) {
      // Remove handlers to prevent onclose from triggering reconnect
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  /**
   * Permanently destroy this instance.
   * Disconnects, removes listeners, and prevents future reconnects.
   */
  destroy(): void {
    this.destroyed = true;
    this.disconnect();
    this.removeVisibilityListener();
  }

  /**
   * Get the last known event ID (for external tracking).
   */
  getLastEventId(): number | null {
    return this.lastEventId;
  }

  /**
   * Get current connection status.
   */
  getStatus(): CrmWebSocketStatus {
    return this.status;
  }

  /**
   * Subscribe to restricted entity types.
   * If connected, sends the subscribe message immediately.
   * The subscription is stored and re-sent automatically on reconnect.
   *
   * Entity types should be hardcoded type names (e.g., 'ahoy_meeting'),
   * not derived from untrusted input. The backend validates subscriptions
   * against user permissions.
   */
  subscribe(entityTypes: EntityTypeName[]): void {
    if (this.destroyed) {
      return;
    }
    this.currentSubscription = [...entityTypes];
    this.send({ type: 'subscribe', entity_types: entityTypes });
  }

  /**
   * Unsubscribe from all restricted entity types.
   * Clears stored subscription and sends unsubscribe if connected.
   */
  unsubscribe(): void {
    if (this.destroyed) {
      return;
    }
    this.currentSubscription = null;
    this.send({ type: 'unsubscribe' });
  }

  // --- Internal ---

  private handleMessage(message: CrmServerMessage): void {
    switch (message.type) {
      case 'crm.events.batch': {
        const { events } = message;
        if (events.length > 0) {
          // Track the highest event_id we've seen
          // Use reduce to avoid RangeError with large batches (Math.max spread exceeds call stack limit)
          const maxEventId = events.reduce(
            (max, e) => Math.max(max, e.event_id),
            -Infinity
          );
          if (this.lastEventId === null || maxEventId > this.lastEventId) {
            this.lastEventId = maxEventId;
          }
          this.receivedEventSinceLastPing = true;

          this.config.onEvents(events);
        }

        // Advance the cursor to the server watermark echoed on a catchup reply,
        // even when the access-filtered event list is empty. Without this, a
        // catchup that returns [] leaves lastEventId behind the org watermark and
        // gap-detection re-fires every heartbeat forever. Live-broadcast batches
        // omit latest_event_id, so this is a no-op on the live path. Do NOT set
        // receivedEventSinceLastPing here — an empty catchup reply says nothing
        // about live-broadcast health.
        const watermark = message.latest_event_id;
        if (watermark != null) {
          const confirmed =
            this.lastEventId === null
              ? watermark
              : Math.max(this.lastEventId, watermark);
          this.lastEventId = confirmed;
          // Notify even when `confirmed === initialLastEventId` (an idle wake
          // whose catchup is empty and whose watermark equals the resumed
          // cursor): the hook must seed its poller-facing ref from a CONFIRMED
          // cursor, not only from one that strictly advances.
          this.config.onCursorConfirmed?.(confirmed);
        }
        break;
      }

      case 'full_refresh_required':
        this.config.onFullRefreshRequired();
        // Reset lastEventId so the next connect() starts fresh without requesting
        // catchup from a stale event ID (which would send redundant events on top
        // of the already-refreshed data).
        this.lastEventId = null;
        break;

      case 'pong': {
        const heartbeatEventId = message.latest_event_id;

        // Seed the baseline from the first pong when we have no cursor yet.
        // This is the org's current watermark; the page's initial HTTP fetches
        // already cover everything <= it, so we adopt it WITHOUT catchup.
        if (this.lastEventId === null && heartbeatEventId != null) {
          this.lastEventId = heartbeatEventId;
          // Mirror the seeded cursor to the hook so a connected-but-quiet WS
          // (fresh connect, no events) doesn't read as inactive to the poller.
          this.config.onCursorConfirmed?.(heartbeatEventId);
          break;
        }

        // Zombie detection: the heartbeat (a direct reply that reached us) reports
        // the org advanced past our cursor, yet no event frames arrived this
        // interval -> live broadcasts are not reaching us. Fill the gap via catchup
        // (idempotent; its reply travels the same direct-send path the pong did).
        if (
          heartbeatEventId != null &&
          this.lastEventId !== null &&
          heartbeatEventId > this.lastEventId &&
          !this.receivedEventSinceLastPing
        ) {
          console.warn(
            '[CrmWebSocket] Heartbeat reports newer events than received; requesting catchup',
            { heartbeatEventId, lastEventId: this.lastEventId }
          );
          this.send({ type: 'catchup', last_event_id: this.lastEventId });
        }
        break;
      }
    }
  }

  private send(message: CrmClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private setStatus(status: CrmWebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.config.onStatusChange?.(status);
    }
  }

  // --- Reconnection ---

  private scheduleReconnect(): void {
    if (this.destroyed) {
      return;
    }

    this.clearReconnectTimer();

    const delay = withJitter(
      Math.min(
        MIN_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
        MAX_RECONNECT_DELAY_MS
      )
    );
    this.reconnectAttempt++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // --- Heartbeat ---

  /**
   * Start the heartbeat: every HEARTBEAT_INTERVAL_MS, send a ping message
   * to the server and arm a HEARTBEAT_TIMEOUT_MS timeout. If no message
   * arrives before the timeout fires, the connection is considered dead and
   * we close to trigger reconnection. Any incoming message (including the
   * pong response) cancels the timeout.
   *
   * The JS WebSocket API cannot send protocol-level pings, so we use
   * application-level ping/pong messages.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      // Open a fresh detection window for this heartbeat before sending the
      // ping, so a pong later this interval can tell whether any event batch
      // arrived while we were waiting for the reply.
      this.receivedEventSinceLastPing = false;

      // Send application-level ping to the server
      this.send({ type: 'ping' });

      // Clear any existing timeout before re-arming to prevent timer leaks
      // after system sleep/wake when queued timers can fire out of order.
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }

      // Arm the dead-connection timeout; cancelled in onmessage if data arrives
      this.heartbeatTimeoutTimer = setTimeout(() => {
        console.warn('[CrmWebSocket] Heartbeat timeout, reconnecting');
        this.ws?.close();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  // --- Visibility / Inactivity ---

  private handleVisibilityChange = (): void => {
    if (this.destroyed) {
      return;
    }

    if (document.hidden) {
      this.hiddenSince = Date.now();
    } else {
      // Tab became visible again
      if (this.hiddenSince) {
        const elapsed = Date.now() - this.hiddenSince;
        this.hiddenSince = null;

        // Reuses the shared idle threshold; here it gates the tab-HIDDEN-duration
        // stale-refresh (a distinct policy that happens to share the same 10-min value).
        if (elapsed >= USER_IDLE_THRESHOLD_MS) {
          // Been hidden too long — local state may be very stale.
          // Discard it and refetch everything.
          this.config.onFullRefreshRequired();
          // Reset lastEventId so the next connect() starts fresh without requesting
          // catchup from a stale event ID (which would send redundant events on top
          // of the already-refreshed data).
          this.lastEventId = null;
        }
      }

      // If we somehow lost the connection while hidden, reconnect now
      if (
        this.status === 'disconnected' &&
        !this.shouldNotReconnect &&
        !this.destroyed
      ) {
        // Cancel any pending reconnect timer — we want to reconnect immediately
        // since the user is actively looking at the page
        this.clearReconnectTimer();
        this.reconnectAttempt = 0; // Reset backoff since user is actively looking
        this.connect();
      }
    }
  };

  private setupVisibilityListener(): void {
    // Remove any existing listener first to make this idempotent
    // (connect() is called on every reconnect)
    this.removeVisibilityListener();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private removeVisibilityListener(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }
}
